import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "../styles/adminKyc.css";

const API_BASE_URL = "https://growrise.shop";

export default function AdminKyc() {
  const [kycList, setKycList] = useState([]);
  const [loading, setLoading] = useState(true);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const clearAuthStorage = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("naxoraToken");
    localStorage.removeItem("naxoraAuth");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("naxoraCurrentUser");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("user");
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Please login again",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    clearAuthStorage();
    window.location.replace("/login");
  };

  const normalizeStatus = (status) => String(status || "").toLowerCase();

  const fetchKyc = async () => {
    try {
      setLoading(true);

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/kyc/all`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401) {
        await handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || "Failed to load KYC requests");
      }

      setKycList(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to load KYC requests",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKyc();
  }, []);

  const handleApprove = async (id) => {
    try {
      const confirm = await Swal.fire({
        title: "Approve KYC?",
        text: "This user will be marked as verified.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Approve",
        confirmButtonColor: "#d4af37",
        cancelButtonColor: "#6b7280",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      if (!confirm.isConfirmed) return;

      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/kyc/approve/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401) {
        await handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || "Failed to approve KYC");
      }

      await Swal.fire({
        icon: "success",
        title: "Approved",
        text: "KYC approved successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      fetchKyc();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to approve KYC",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const handleReject = async (id) => {
    try {
      const rejectPrompt = await Swal.fire({
        title: "Reject KYC?",
        input: "text",
        inputLabel: "Reason",
        inputPlaceholder: "Enter rejection reason",
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#d4af37",
        cancelButtonColor: "#6b7280",
        background: "#0b0b0b",
        color: "#ffffff",
        inputAttributes: {
          autocapitalize: "off",
          autocorrect: "off",
        },
        preConfirm: (value) => {
          if (!String(value || "").trim()) {
            Swal.showValidationMessage("Rejection reason is required");
          }
          return value;
        },
      });

      if (!rejectPrompt.isConfirmed) return;

      const reason = String(rejectPrompt.value || "").trim();
      const token = getToken();

      if (!token) {
        await handleUnauthorized();
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/kyc/reject/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.status === 401) {
        await handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(data.message || "Failed to reject KYC");
      }

      await Swal.fire({
        icon: "success",
        title: "Rejected",
        text: "KYC rejected successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      fetchKyc();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Unable to reject KYC",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const renderStatusText = (status) => {
    const value = normalizeStatus(status);

    if (value === "verified") return "Verified";
    if (value === "pending") return "Pending";
    if (value === "rejected") return "Rejected";
    return "Unverified";
  };

  if (loading) {
    return <div className="admin-kyc-loading">Loading...</div>;
  }

  return (
    <div className="admin-kyc-page">
      <h1>KYC Requests</h1>

      {kycList.length === 0 ? (
        <div className="admin-kyc-loading">No KYC requests found</div>
      ) : (
        <div className="admin-kyc-grid">
          {kycList.map((kyc) => {
            const status = normalizeStatus(kyc.status);

            return (
              <div key={kyc.id} className="admin-kyc-card">
                <h3>{kyc.fullName || "No Name"}</h3>
                <p>{kyc.email || "-"}</p>
                <p>{kyc.phone || "-"}</p>
                <p>{kyc.country || "-"}</p>
                <p>{kyc.city || "-"}</p>
                <p>{kyc.dateOfBirth || "-"}</p>

                <div className="admin-kyc-images">
                  <img src={kyc.idFrontUrl} alt="ID Front" />
                  <img src={kyc.idBackUrl} alt="ID Back" />
                  <img src={kyc.selfieUrl} alt="Selfie" />
                </div>

                <div className="admin-kyc-status">
                  Status: {renderStatusText(status)}
                </div>

                {status === "rejected" && kyc.rejectionReason ? (
                  <div className="admin-kyc-status">
                    Reason: {kyc.rejectionReason}
                  </div>
                ) : null}

                {status === "pending" && (
                  <div className="admin-kyc-actions">
                    <button
                      className="approve-btn"
                      onClick={() => handleApprove(kyc.id)}
                    >
                      Approve
                    </button>

                    <button
                      className="reject-btn"
                      onClick={() => handleReject(kyc.id)}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}